/**
 * Aba "Checklist - Milhões" da ficha da cliente, idêntica à do legado (`admin.html:2639-2644`,
 * `5149-5164`, `5365-5389`; linha 199 do inventário, e a trava de botão da linha 179).
 *
 * Os 77 itens do catálogo fixo (elemento e efeito), cada um com a caixa de marcar; o salvar grava
 * os 77 booleanos, um por coluna, com "Salvando...", "Salvo!" (some em 3 s) ou "Erro ao salvar: "
 * com a mensagem crua.
 *
 * Território da frente A7. A moldura importa esta aba pelo nome `AbaMilhoes`.
 */

// Estado e efeito do React.
import { useEffect, useState } from 'react';
// O botão do Chassi.
import { Botao } from '@complexity/ui';
// A leitura e a gravação do checklist (A2).
import { lerChecklistMilhoes, salvarChecklistMilhoes } from '@studiolu/dados/areas/ficha';
// A fonte de dados do painel.
import { usePainel } from '../../../contexto.js';
// O parágrafo do status, peça comum (o texto em si vive com a página, ver `status-da-pagina.ts`).
import { LinhaDeStatus } from '../../../comum/status-temporario.js';
// O status que vive com a página, e não com a aba (achado da verificação da A7, linha 199).
import { criarStatusDaPagina, useStatusDaPagina } from './coloracao/status-da-pagina.js';
// O contrato da aba.
import type { FichaAberta, PropsDaAba } from '../comum-ficha/tipos.js';
// A caixa de marcar isolada, com rótulo e apoio (A2).
import { CaixaDeMarcacao } from '../comum-ficha/Marcacoes.js';
// O catálogo fixo dos 77 itens (A2).
import { ITENS_MILHOES } from '../constantes.js';
// O escopo estável, o recorte por papel e o aviso de ficha ausente, das abas da ficha (A3).
import { useEscopoEstavel } from './comum-a3/escopo.js';
import { useRecortePorPapel } from './comum-a3/papel.js';
import { AbaSemFicha } from './comum-a3/sem-ficha.js';

// O `#status-milhoes` do legado (`admin.html:2643`): um só, criado com o módulo e vivo com a página.
const statusMilhoes = criarStatusDaPagina();

/** As marcas da tela, por coluna. Coluna ausente vale desmarcada. */
type Marcas = Readonly<Record<string, boolean>>;

/** A linha lida vira as marcas: `!!data?.[item.col]` para cada item (`admin.html:5374-5376`). */
function marcasDaLinha(linha: Readonly<Record<string, unknown>> | null): Marcas {
  return Object.fromEntries(ITENS_MILHOES.map((item) => [item.col, !!linha?.[item.col]]));
}

export function AbaMilhoes({ ficha }: PropsDaAba) {
  // Sem a ficha aberta, a aba não consulta nada (ver `sem-ficha.tsx`).
  if (!ficha) return <AbaSemFicha aba="Checklist - Milhões" />;
  return <ConteudoMilhoes ficha={ficha} />;
}

function ConteudoMilhoes({ ficha }: { readonly ficha: FichaAberta }) {
  const { dados } = usePainel();
  const { somenteLeitura } = useRecortePorPapel();
  const escopo = useEscopoEstavel(ficha);
  const [marcas, setMarcas] = useState<Marcas>(() => marcasDaLinha(null));
  // O "Salvar" nasce travado e só destrava depois da leitura (linha 179, `admin.html:5975` e
  // `5377`): salvar antes gravaria as marcas que estivessem na tela, de outra cliente.
  const [carregado, setCarregado] = useState(false);
  const status = useStatusDaPagina(statusMilhoes);

  // A leitura ao abrir a aba, como o `carregarChecklistMilhoes` do legado (`admin.html:5365-5378`).
  useEffect(() => {
    // Uma resposta que chega depois de a aba trocar de cliente não escreve na tela.
    let valendo = true;
    setCarregado(false);
    // O legado limpa o status antes de ler (`5366`). Só este ponto e o salvar escrevem nele: uma
    // gravação ainda em curso que responder depois daqui escreve por cima, como no legado.
    statusMilhoes.escrever('');
    void lerChecklistMilhoes(dados, escopo).then((resultado) => {
      if (!valendo) return;
      // DEFEITO DO LEGADO REPRODUZIDO: falha na leitura só vai para o console, as 77 caixas voltam
      // desmarcadas e o "Salvar" destrava, então salvar gravaria tudo falso por cima (`5372-5377`).
      if (!resultado.ok) console.error('Erro ao carregar checklist de milhões:', resultado.erro.mensagem);
      setMarcas(marcasDaLinha(resultado.ok ? resultado.dados : null));
      setCarregado(true);
    });
    return () => {
      valendo = false;
    };
  }, [dados, escopo]);

  // "Salvar checklist de milhões" (`admin.html:5380-5389`). A resposta escreve no parágrafo da
  // página mesmo com a aba fora da tela, como o legado escreve no nó do DOM (`5387`).
  const salvar = async () => {
    statusMilhoes.escrever('Salvando...');
    // Os 77 booleanos, um por coluna, na ordem do catálogo (`5383-5385`).
    const registro = Object.fromEntries(ITENS_MILHOES.map((item) => [item.col, !!marcas[item.col]]));
    const resultado = await salvarChecklistMilhoes(dados, escopo, registro);
    // Este ponto do legado mostra a mensagem CRUA depois do prefixo, e não a tradução.
    if (!resultado.ok) {
      statusMilhoes.escrever('Erro ao salvar: ' + resultado.erro.mensagem);
      return;
    }
    statusMilhoes.mostrar('Salvo!');
  };

  return (
    <section className="flex flex-col gap-cx-3" data-testid="aba-milhoes">
      <h3 className="text-cx-base font-semibold text-texto">Checklist de Milhões</h3>
      {/* A lista com a linha de cima e uma linha embaixo de cada item, como a `.milhoes-lista` (`762-765`). */}
      <div className="border-t border-borda">
        {ITENS_MILHOES.map((item) => (
          <div key={item.col} className="flex min-h-toque flex-col justify-center border-b border-borda py-cx-2">
            <CaixaDeMarcacao
              rotulo={item.elemento}
              apoio={item.efeito}
              marcada={!!marcas[item.col]}
              aoMudar={(marcada) => setMarcas((m) => ({ ...m, [item.col]: marcada }))}
              // Consulta: caixa desabilitada (`admin.html:3394`).
              desabilitado={somenteLeitura}
            />
          </div>
        ))}
      </div>
      {/* Consulta não vê botão de ação (`admin.html:3395`). O botão NÃO trava durante o envio, como
          no legado (`5380-5389`). */}
      {!somenteLeitura && (
        <div className="mt-cx-2">
          <Botao disabled={!carregado} onClick={() => void salvar()}>
            Salvar checklist de milhões
          </Botao>
        </div>
      )}
      <LinhaDeStatus texto={status} testid="status-milhoes" />
    </section>
  );
}
